interface Dataset {
  label: string;
  data: number[];
  backgroundColor: string;
}

interface ChartData {
  labels: string[];
  datasets: Dataset[];
}

type OperationType = 'count' | 'sum';
type SortBy = 'value' | 'label' | 'none';
type SortOrder = 'asc' | 'desc';

interface CreateDatasetOptions<T> {
  data: T[];
  label: string;
  backgroundColor?: string;
  operation: OperationType;
  // For counting: the key to group by
  groupByKey?: keyof T;
  // For summing: the key to sum values
  valueKey?: keyof T;
  // For primitive arrays (strings, numbers): direct counting
  countPrimitives?: boolean;
  // Sorting and limiting options
  sortBy?: SortBy;
  sortOrder?: SortOrder;
  limit?: number;
  includeOthers?: boolean;
  othersLabel?: string;
}

/**
 * Creates a ChartData object for horizontal bar charts from an array of data
 * Can count occurrences or sum values based on specified keys
 */
export function createDataset<T = any>(options: CreateDatasetOptions<T>): ChartData {
  const {
    data,
    label,
    backgroundColor = '#36A2EB',
    operation,
    groupByKey,
    valueKey,
    countPrimitives = false,
    sortBy = 'value',
    sortOrder = 'desc',
    limit,
    includeOthers = false,
    othersLabel = 'Others'
  } = options;

  const processedData: { [key: string]: number } = {};

  if (operation === 'count') {
    if (countPrimitives && (typeof data[0] === 'string' || typeof data[0] === 'number')) {
      // Count primitive values directly
      data.forEach(item => {
        const key = String(item);
        processedData[key] = (processedData[key] || 0) + 1;
      });
    } else if (groupByKey) {
      // Count occurrences by grouping key
      data.forEach(item => {
        const key = String(item[groupByKey]);
        processedData[key] = (processedData[key] || 0) + 1;
      });
    } else {
      throw new Error('For count operation, either set countPrimitives=true or provide groupByKey');
    }
  } else if (operation === 'sum') {
    if (!groupByKey || !valueKey) {
      throw new Error('For sum operation, both groupByKey and valueKey are required');
    }

    // Sum values by grouping key
    data.forEach(item => {
      const key = String(item[groupByKey]);
      const value = Number(item[valueKey]) || 0;
      processedData[key] = (processedData[key] || 0) + value;
    });
  }

  // Convert to array of objects for sorting
  let dataArray = Object.entries(processedData).map(([key, value]) => ({
    label: key,
    value
  }));

  // Sort the data
  if (sortBy === 'value') {
    dataArray.sort((a, b) => sortOrder === 'asc' ? a.value - b.value : b.value - a.value);
  } else if (sortBy === 'label') {
    dataArray.sort((a, b) => sortOrder === 'asc' ? a.label.localeCompare(b.label) : b.label.localeCompare(a.label));
  }

  // Apply limit and handle "Others" category
  if (limit && dataArray.length > limit) {
    const topItems = dataArray.slice(0, limit);

    if (includeOthers) {
      const remainingItems = dataArray.slice(limit);
      const othersValue = remainingItems.reduce((sum, item) => sum + item.value, 0);

      if (othersValue > 0) {
        topItems.push({
          label: othersLabel,
          value: othersValue
        });
      }
    }

    dataArray = topItems;
  }

  // Extract final labels and data arrays
  const labels = dataArray.map(item => item.label);
  const dataValues = dataArray.map(item => item.value);

  const dataset: Dataset = {
    label,
    data: dataValues,
    backgroundColor
  };

  return {
    labels,
    datasets: [dataset]
  };
}

/**
 * Simplified function for counting occurrences of primitive values
 */
export function createCountDataset(
  data: (string | number)[],
  label: string,
  backgroundColor?: string,
  options?: {
    sortBy?: SortBy;
    sortOrder?: SortOrder;
    limit?: number;
    includeOthers?: boolean;
    othersLabel?: string;
  }
): ChartData {
  return createDataset({
    data,
    label,
    backgroundColor,
    operation: 'count',
    countPrimitives: true,
    ...options
  });
}

/**
 * Simplified function for counting occurrences by a specific key
 */
export function createCountByKeyDataset<T>(
  data: T[],
  groupByKey: keyof T,
  label: string,
  backgroundColor?: string,
  options?: {
    sortBy?: SortBy;
    sortOrder?: SortOrder;
    limit?: number;
    includeOthers?: boolean;
    othersLabel?: string;
  }
): ChartData {
  return createDataset({
    data,
    label,
    backgroundColor,
    operation: 'count',
    groupByKey,
    ...options
  });
}

/**
 * Simplified function for summing values by a specific key
 */
export function createSumDataset<T>(
  data: T[],
  groupByKey: keyof T,
  valueKey: keyof T,
  label: string,
  backgroundColor?: string,
  options?: {
    sortBy?: SortBy;
    sortOrder?: SortOrder;
    limit?: number;
    includeOthers?: boolean;
    othersLabel?: string;
  }
): ChartData {
  return createDataset({
    data,
    label,
    backgroundColor,
    operation: 'sum',
    groupByKey,
    valueKey,
    ...options
  });
}

/**
 * Helper function to combine multiple datasets into a single ChartData object
 */
export function combineDatasets(chartDataArray: ChartData[]): ChartData {
  if (chartDataArray.length === 0) {
    return { labels: [], datasets: [] };
  }

  // Use the first chart's labels as the base
  const labels = chartDataArray[0].labels;
  const datasets: Dataset[] = [];

  // Combine all datasets from all charts
  chartDataArray.forEach(chartData => {
    datasets.push(...chartData.datasets);
  });

  return { labels, datasets };
}
